import React, { useCallback, useEffect, useState } from 'react';
import { Download } from 'lucide-react';
import { cn } from '../cn';
import Button from '../Button';
import { NumberedPagination } from '../pagination/NumberedPagination';
import { PortalCashTransactionFiltersBar } from './PortalCashTransactionFiltersBar';
import { PortalCashTransactionSummaryGrid } from './PortalCashTransactionSummaryGrid';
import { PortalCashTransactionTable } from './PortalCashTransactionTable';
import { defaultCashTransactionFilters } from './formatters';
import { CASH_PAGE_SIZE_OPTIONS } from './constants';
const UI_SORT_TO_API = {
    creditAmount: 'credits',
    cashReceived: 'cash',
    buyerCostRate: 'cost',
    createdAt: 'created',
};
function resolveCashApiSort(sortActiveKey, sortDirection) {
    const sort = (sortActiveKey && UI_SORT_TO_API[sortActiveKey]) || 'created';
    const sortDir = sortDirection === 'ASC' ? 'asc' : 'desc';
    return { sort, sortDir };
}
export const PortalCashTransactionsLedger = ({ title = 'Cash Transactions', description = 'History of cash credit sales in the hierarchy.', loading, data, onFetch, onViewDetail, onExport, exporting, exportDisabled, className, headerExtra, hideFlowFilter, initialFilters, summaryVariant = 'default', showCreatedColumn = false, sortable = false, sortActiveKey = null, sortDirection = null, onSort, sortFetchTick = 0, }) => {
    var _a, _b;
    const [draft, setDraft] = useState(initialFilters !== null && initialFilters !== void 0 ? initialFilters : defaultCashTransactionFilters());
    const [applied, setApplied] = useState(initialFilters !== null && initialFilters !== void 0 ? initialFilters : defaultCashTransactionFilters());
    const [page, setPage] = useState(1);
    const [limit, setLimit] = useState(20);
    const runFetch = useCallback((nextApplied, nextPage, nextLimit, sortKey = sortActiveKey, sortDirUi = sortDirection) => {
        const { sort, sortDir } = resolveCashApiSort(sortKey, sortDirUi);
        void onFetch({
            filters: nextApplied,
            page: nextPage,
            limit: nextLimit,
            sort,
            sortDir,
        });
    }, [onFetch, sortActiveKey, sortDirection]);
    useEffect(() => {
        runFetch(applied, page, limit);
    }, []);
    useEffect(() => {
        if (!sortable || sortFetchTick === 0)
            return;
        setPage(1);
        // Props for this render already include the host's new sort (batched with tick).
        runFetch(applied, 1, limit, sortActiveKey, sortDirection);
        // eslint-disable-next-line react-hooks/exhaustive-deps -- host-driven refetch on tick only
    }, [sortFetchTick]);
    const handleApply = () => {
        setApplied(draft);
        setPage(1);
        runFetch(draft, 1, limit);
    };
    const handlePageChange = (p) => {
        setPage(p);
        runFetch(applied, p, limit);
    };
    const handleLimitChange = (nextLimit) => {
        setLimit(nextLimit);
        setPage(1);
        runFetch(applied, 1, nextLimit);
    };
    const items = (_a = data === null || data === void 0 ? void 0 : data.items) !== null && _a !== void 0 ? _a : [];
    const pagination = data === null || data === void 0 ? void 0 : data.pagination;
    const summary = (_b = data === null || data === void 0 ? void 0 : data.summary) !== null && _b !== void 0 ? _b : null;
    return (React.createElement("div", { className: cn('space-y-3', className) },
        React.createElement("div", { className: "flex flex-wrap items-start justify-between gap-2 px-4 pt-4" },
            React.createElement("div", null,
                React.createElement("h2", { className: "text-lg font-semibold" }, title),
                description ? React.createElement("p", { className: "mt-0.5 text-xs text-muted-foreground" }, description) : null),
            React.createElement("div", { className: "flex items-center gap-2" },
                headerExtra,
                onExport ? (React.createElement(Button, { type: "button", variant: "outline", size: "sm", disabled: exportDisabled || exporting, onClick: () => void onExport({ filters: applied }) },
                    React.createElement(Download, { className: "mr-1.5 h-4 w-4" }),
                    exporting ? 'Exporting…' : 'Export CSV')) : null)),
        React.createElement("div", { className: "px-4" },
            React.createElement(PortalCashTransactionSummaryGrid, { summary: summary, filteredCount: pagination === null || pagination === void 0 ? void 0 : pagination.total, variant: summaryVariant })),
        React.createElement("div", { className: "px-4" },
            React.createElement(PortalCashTransactionFiltersBar, { draft: draft, onDraftChange: setDraft, onApply: handleApply, applying: loading, hideFlowFilter: hideFlowFilter })),
        React.createElement(PortalCashTransactionTable, { rows: items, loading: loading, onViewDetail: onViewDetail, showCreatedColumn: showCreatedColumn, sortable: sortable, sortActiveKey: sortActiveKey, sortDirection: sortDirection, onSort: onSort }),
        pagination && pagination.totalPages > 0 ? (React.createElement("div", { className: "flex flex-wrap items-center justify-between gap-2 border-t border-border px-4 py-3" },
            React.createElement("div", { className: "flex items-center gap-2 text-xs text-muted-foreground" },
                React.createElement("span", null, "Rows per page"),
                React.createElement("select", { className: "rounded-md border border-input bg-background px-2 py-1 text-xs", value: limit, onChange: (e) => handleLimitChange(Number(e.target.value)) }, CASH_PAGE_SIZE_OPTIONS.map((n) => (React.createElement("option", { key: n, value: n }, n))))),
            React.createElement(NumberedPagination, { currentPage: pagination.page, totalPages: pagination.totalPages, onPageChange: handlePageChange, variant: "portal" }))) : null));
};
